import { useEffect } from 'react';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';
import CTA from '../../home/components/CTA';
import { useSEO, getServiceSchema } from '../../../utils/seo';

export default function WebDesignPage() {
  useSEO({
    title: 'Web Design for Contractors | Custom Contractor Websites | Million Dollar Contractors',
    description: 'Professional web design services for contractors. Mobile-responsive, conversion-optimized websites that generate leads 24/7. Custom designs for electrical, HVAC, plumbing, roofing, and landscaping contractors.',
    keywords: 'contractor web design, contractor website design, home service web design, mobile responsive contractor websites, conversion optimized contractor sites',
    canonical: '/services/web-design',
    schema: getServiceSchema(
      'Web Design for Contractors',
      'Custom-built, conversion-optimized websites that turn visitors into customers. Mobile-responsive designs that showcase your expertise and build trust instantly.'
    )
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    window.location.href = '/#contact';
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-black via-gray-900 to-black overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-600/10 via-transparent to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-600 rounded-2xl mb-6">
              <i className="ri-layout-grid-line text-4xl text-white"></i>
            </div>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6">
              Web Design for Contractors
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Custom-built, conversion-optimized websites that turn visitors into customers. Mobile-responsive designs that showcase your expertise and build trust instantly.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button onClick={scrollToContact} className="inline-flex items-center px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all whitespace-nowrap cursor-pointer">
                Get Started <i className="ri-arrow-right-line ml-2"></i>
              </button>
              <a href="#features" className="inline-flex items-center px-8 py-4 bg-white text-black font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer">
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-4">
              What's Included
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to dominate your market online
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-50 p-8 rounded-2xl hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-smartphone-line text-2xl text-red-600"></i>
              </div>
              <h3 className="font-heading text-xl font-bold text-black mb-3">Mobile-Responsive Design</h3>
              <p className="text-gray-600">
                Perfect display on all devices - phones, tablets, and desktops. Your customers can reach you anywhere.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-line-chart-line text-2xl text-red-600"></i>
              </div>
              <h3 className="font-heading text-xl font-bold text-black mb-3">Conversion-Optimized</h3>
              <p className="text-gray-600">
                Strategic layouts designed to turn visitors into leads with clear calls-to-action and trust signals.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-speed-line text-2xl text-red-600"></i>
              </div>
              <h3 className="font-heading text-xl font-bold text-black mb-3">Fast Loading Speeds</h3>
              <p className="text-gray-600">
                Lightning-fast performance keeps visitors engaged and improves your search engine rankings.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-search-line text-2xl text-red-600"></i>
              </div>
              <h3 className="font-heading text-xl font-bold text-black mb-3">SEO-Friendly Structure</h3>
              <p className="text-gray-600">
                Built with search engines in mind to help you rank higher and get found by more customers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-4">
              Our Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From concept to launch in 4 simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', desc: 'We learn about your business, goals, and target customers' },
              { step: '02', title: 'Design', desc: 'Custom design mockups tailored to your brand and industry' },
              { step: '03', title: 'Development', desc: 'Build your site with cutting-edge technology and best practices' },
              { step: '04', title: 'Launch', desc: 'Go live with ongoing support and optimization' }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                  <div className="text-6xl font-heading font-bold text-red-600/20 mb-4">{item.step}</div>
                  <h3 className="font-heading text-2xl font-bold text-black mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-6">
                Why Your Website Matters
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Your website is often the first impression potential customers have of your business. Make it count.
              </p>
              <div className="space-y-6">
                {[
                  { icon: 'ri-check-line', text: 'Build instant credibility and trust with professional design' },
                  { icon: 'ri-check-line', text: 'Capture leads 24/7 even when you\'re not working' },
                  { icon: 'ri-check-line', text: 'Stand out from competitors with a unique online presence' },
                  { icon: 'ri-check-line', text: 'Showcase your work with stunning project galleries' },
                  { icon: 'ri-check-line', text: 'Make it easy for customers to contact you and book services' }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-white`}></i>
                    </div>
                    <p className="text-lg text-gray-700">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/0450e611465bb2f97a939ff78f43f881.jpg"
                alt="Web Design Showcase"
                className="w-full h-auto rounded-2xl shadow-2xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <CTA />
      <Footer />
    </div>
  );
}
